import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, RAYG, RAYG_TEXT } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

export default function Workflows() {
  const [wfs, setWfs] = useState(null);
  const [depts, setDepts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", trigger: "", outcome: "" });

  const load = () => Promise.all([api.get("/workflows"), api.get("/departments")]).then(([w, d]) => { setWfs(w.data); setDepts(d.data); });
  useEffect(() => { load(); }, []);

  const deptColor = Object.fromEntries(depts.map((d) => [d.key, d.color]));
  const create = async (e) => {
    e.preventDefault();
    await api.post("/workflows", form);
    toast.success("Workflow created — open it to add cross-functional steps");
    setShowNew(false); setForm({ name: "", description: "", trigger: "", outcome: "" });
    load();
  };

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="workflows-page">
      <PageHeader overline="Workflow Mapping Engine" title="Cross-Functional Workflows"
        subtitle="End-to-end workflows crossing departments, with hand-offs, flags and AI-redesign classification per step."
        actions={<button onClick={() => setShowNew(true)} data-testid="new-workflow-btn"
          className="flex items-center gap-1.5 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold hover:bg-black/85 transition-colors"><Plus size={14} /> New workflow</button>} />
      {showNew && (
        <form onSubmit={create} className="mx-8 mb-6 bg-white border border-[#D4AF37]/60 rounded-sm p-5 grid grid-cols-2 gap-3 fade-up" data-testid="new-workflow-form">
          <input required placeholder="Workflow name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="workflow-name-input"
            className="col-span-2 border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <input placeholder="Trigger" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}
            className="border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <input placeholder="Intended outcome" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
            className="border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <div className="col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowNew(false)} className="text-xs border border-black/20 rounded-sm px-3 py-2">Cancel</button>
            <button type="submit" data-testid="create-workflow-submit" className="text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold">Create</button>
          </div>
        </form>
      )}
      <div className="px-8 space-y-4">
        {wfs === null && <p className="text-black/50 text-sm">Loading workflows…</p>}
        {wfs?.map((w) => (
          <Link to={`/workflows/${w.id}`} key={w.id} data-testid={`workflow-card-${w.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="chip block bg-white border border-black/10 rounded-sm p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">{w.name}</h3>
                <p className="text-[13px] text-black/55 mt-0.5">{w.description}</p>
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  {w.departments.map((dk) => (
                    <span key={dk} className="w-3.5 h-3.5 rounded-sm inline-block" title={dk} style={{ background: deptColor[dk] || "#999" }} />
                  ))}
                  <span className="text-[11px] text-black/45 ml-1">{w.departments.length} departments · {w.steps.length} steps · cycle {w.cycle_days}d</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-sm font-mono font-medium px-2.5 py-1.5 rounded-sm" style={{ background: RAYG(w.health), color: RAYG_TEXT(w.health) }}>
                  health {w.health}
                </span>
                <p className="text-[11px] text-black/50 mt-2">{Object.values(w.flag_summary).reduce((a, b) => a + b, 0)} issues flagged</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
